/**
 * Shipping customer actions — gated on fulfillmentMode=SHIPPING (S23).
 * Ack CTA when SHIPPED (unchanged). Late CTA only after deadline within window
 * when lateComplaintFiledAt / lateComplaintEverFiled are null/false.
 * Permanent hide after report is server-driven (invalidate + reconstruct) — G6.
 */
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button.js';
import { toCustomerFacingErrorMessage } from '../../../shared/errors/toCustomerFacingErrorMessage.js';
import {
  useCustomerAckShippingReceipt,
  useCustomerReportShippingProblem,
} from '../../orders/ordersApi.js';
import { isShippingLateComplaintCtaVisible } from './shippingLateComplaintVisibility.js';

export interface ShippingOrderActionsPanelProps {
  readonly transactionId: number;
  readonly fulfillmentId: number;
  readonly fulfillmentMode: 'PICKUP' | 'SHIPPING' | string | null | undefined;
  readonly status: string | null | undefined;
  readonly version: number;
  readonly onAckSuccess?: () => void;
  /** Reconstruct live + detail after late complaint so remount stays hidden (G6). */
  readonly onReportSuccess?: () => void;
  /** Sealed snapshot plumbing (display owned by SealedShippingPolicyPanel). */
  readonly shippingMethodSnapshot?: unknown | null;
  readonly promisedDeliveryByAt?: string | null;
  readonly autoDeliverEligibleAt?: string | null;
  readonly lateComplaintFiledAt?: string | null;
  readonly lateComplaintEverFiled?: boolean;
  /** Stamped at MARK_SHIPPED — informational for FE workers / sealed panels. */
  readonly tenantHandoffKind?:
    | 'DISPATCHED_TO_CARRIER'
    | 'ARRIVED_AT_PARCEL_PICKUP_POINT'
    | 'CUSTOMER_COLLECTED_ASSERTED_BY_TENANT'
    | null;
}

export function ShippingOrderActionsPanel({
  transactionId,
  fulfillmentId,
  fulfillmentMode,
  status,
  version,
  onAckSuccess,
  onReportSuccess,
  shippingMethodSnapshot: _shippingMethodSnapshot,
  promisedDeliveryByAt,
  autoDeliverEligibleAt,
  lateComplaintFiledAt,
  lateComplaintEverFiled,
  tenantHandoffKind: _tenantHandoffKind,
}: ShippingOrderActionsPanelProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const descId = useId();
  const ackMutation = useCustomerAckShippingReceipt();
  const reportMutation = useCustomerReportShippingProblem();
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  /** Session UX until invalidate/reconstruct stamps lateComplaintEverFiled (G6). */
  const [reportDone, setReportDone] = useState(false);

  if (fulfillmentMode !== 'SHIPPING') {
    return null;
  }

  const showAck = status === 'SHIPPED';
  const showLateReport =
    !reportDone &&
    isShippingLateComplaintCtaVisible({
      status,
      promisedDeliveryByAt,
      autoDeliverEligibleAt,
      lateComplaintFiledAt,
      lateComplaintEverFiled,
    });

  if (!showAck && !showLateReport && !reportDone) {
    return null;
  }

  let error: string | null = null;
  if (ackMutation.error != null) {
    error = toCustomerFacingErrorMessage(ackMutation.error, {
      fallback: t('account.orders.shipping.ackError'),
    });
  } else if (reportMutation.error != null) {
    error = toCustomerFacingErrorMessage(reportMutation.error, {
      fallback: t('account.orders.shipping.reportError'),
    });
  }

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-[var(--color-border)] p-4"
      data-testid="shipping-order-actions"
      aria-labelledby={`${descId}-title`}
    >
      {showAck ? (
        <div className="flex flex-col gap-2">
          <h3 id={`${descId}-title`} className="m-0 text-base font-semibold">
            {t('account.orders.shipping.ackTitle')}
          </h3>
          <p className="m-0 text-sm text-[var(--color-on-surface-muted)]">
            {t('account.orders.shipping.ackBody')}
          </p>
          <Button
            type="button"
            intent="primary"
            size="md"
            block
            loading={ackMutation.isPending}
            onClick={() => {
              ackMutation.mutate(
                { transactionId, version },
                { onSuccess: () => onAckSuccess?.() },
              );
            }}
            data-testid="shipping-ack-receipt"
          >
            {ackMutation.isPending
              ? t('account.orders.shipping.ackSubmitting')
              : t('account.orders.shipping.ackCta')}
          </Button>
        </div>
      ) : null}

      {showLateReport || reportDone ? (
        <div className="flex flex-col gap-2">
          <h3 className="m-0 text-base font-semibold">
            {t('account.orders.shipping.reportLateArrivalTitle')}
          </h3>
          <p className="m-0 text-sm text-[var(--color-on-surface-muted)]">
            {t('account.orders.shipping.reportLateArrivalBody')}
          </p>
          {reportDone ? (
            <p className="m-0 text-sm" data-testid="shipping-report-success">
              {t('account.orders.shipping.reportSuccess')}
            </p>
          ) : null}
          {!reportDone && reportOpen ? (
            <>
              <textarea
                className="min-h-[5rem] w-full rounded border border-[var(--color-border)] p-2 text-sm"
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                placeholder={t('account.orders.shipping.reportLateArrivalPlaceholder')}
                data-testid="shipping-report-description"
              />
              <Button
                type="button"
                intent="secondary"
                size="md"
                block
                loading={reportMutation.isPending}
                disabled={reportText.trim().length === 0}
                onClick={() => {
                  reportMutation.mutate(
                    {
                      transactionId,
                      orderFulfillmentId: fulfillmentId,
                      customerDescription: reportText.trim(),
                    },
                    {
                      onSuccess: () => {
                        setReportDone(true);
                        setReportOpen(false);
                        setReportText('');
                        onReportSuccess?.();
                      },
                    },
                  );
                }}
                data-testid="shipping-report-submit"
              >
                {reportMutation.isPending
                  ? t('account.orders.shipping.reportSubmitting')
                  : t('account.orders.shipping.reportLateArrival')}
              </Button>
            </>
          ) : null}
          {!reportDone && !reportOpen ? (
            <Button
              type="button"
              intent="secondary"
              size="md"
              block
              onClick={() => setReportOpen(true)}
              data-testid="shipping-report-open"
            >
              {t('account.orders.shipping.reportLateArrival')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {error != null ? (
        <p className="m-0 text-sm text-[var(--color-danger)]" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
